import { z } from 'zod';

export type ContainerStatus = (typeof containerStatuses)[number];

export const containerStatuses = [
  'created',
  'available',
  'in-use',
  'in-maintenance',
  'out-of-service',
] as const;

export const containerSizes = [
  '10yard',
  '10yard-heavy',
  '20yard',
  '30yard',
  '40yard',
  'concrete-box',
] as const;

export type ContainerTypeName =
  | '10 yard'
  | '10 yard heavy'
  | '20 yard'
  | '30 yard'
  | '40 yard'
  | 'concrete box 10 yard';

export const containerZ = z.object({
  _id: z.string().optional(),
  friendlyId: z.string().optional(),
  archived: z.boolean().optional(),
  status: z.enum(containerStatuses),
  containerTypeInfo: z.object({
    _id: z.string().optional(),
    name: z.string(),
    size: z.enum(containerSizes),
    maxWeight: z.number({
      message: 'Max weight must be a number',
    }),
    volume: z.string(),
    pricePerDay: z.number({
      message: 'Price per day must be a number',
    }),
  }),
});

export type ContainerZ = z.infer<typeof containerZ>;

export const containerImageZ = z.object({
  url: z.string().url('Must be a valid URL'),
  fileName: z.string(),
  contentType: z.string(),
  size: z.number(),
  isMainImage: z.boolean(),
  displayOrder: z.number(),
  archived: z.boolean().optional(),
});

// Add the containerTypeZ schema
export const containerTypeZ = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  size: z.enum(['10yard', '20yard', '30yard', '40yard'], {
    message: 'Please select a valid size',
  }),
  maxWeight: z.number().min(0, { message: 'Max weight must be a positive number' }),
  volume: z.string().optional(),
  pricePerDay: z.number().min(0, { message: 'Price per day must be a positive number' }),
  preAuthAmount: z.number().min(0, { message: 'Pre-auth amount must be a positive number' }),
  capacityDescription: z.string().optional(),
  dimensions: z.string().optional(),
  recommendedUse: z.string().optional(),
  imageUrl: z.string({ message: 'Please enter a valid URL' }).optional(),
  images: z.array(containerImageZ).max(10, 'Maximum 10 images allowed').optional(),
  archived: z.boolean().optional(),
  _id: z.string().optional(),
});

export type ContainerImageZ = z.infer<typeof containerImageZ>;
export type ContainerTypeZ = z.infer<typeof containerTypeZ>;
